import { useState } from 'react'
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot field
  })

  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState('idle') // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('')

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Navn er påkrævet'
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer er påkrævet'
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Ugyldigt telefonnummer (skal være mindst 8 tal)'
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail er påkrævet'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Ugyldig e-mailadresse'
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Beskrivelse/besked er påkrævet'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setStatus('submitting')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        })
      } else {
        throw new Error('Der skete en fejl under afsendelsen. Prøv venligst igen.')
      }
    } catch (error) {
      console.error('Form submission error:', error)
      setStatus('error')
      setErrorMessage(error.message || 'Kunne ikke oprette forbindelse til serveren.')
    }
  }

  return (
    <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#E6DFD3]">
      {status === 'success' ? (
        <div className="text-center py-8 space-y-4 animate-in fade-in duration-300">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 mb-2">
            <CheckCircle className="w-10 h-10" />
          </div>
          <h3 className="font-serif text-2xl font-bold text-[#1E352F]">
            Mange tak for din besked!
          </h3>
          <p className="text-[#1E352F]/80 max-w-md mx-auto text-sm leading-relaxed">
            Vi har modtaget din henvendelse og vil vende tilbage til dig på e-mail eller telefon så hurtigt som muligt – typisk inden for 24 timer på hverdage.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 inline-flex items-center gap-2 bg-[#1E352F] hover:bg-[#2D4A43] text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1">
            <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#1E352F]">
              Send os en besked
            </h3>
            <p className="text-xs sm:text-sm text-[#1E352F]/70">
              Har du spørgsmål til en behandling eller et forløb? Udfyld formularen nedenfor.
            </p>
          </div>

          {status === 'error' && (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-2.5">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Fejl ved afsendelse</p>
                <p className="text-xs text-red-700 mt-0.5">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this field if you are human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Name Field */}
          <div className="space-y-1.5">
            <label htmlFor="name" className="block text-xs font-semibold text-[#1E352F] uppercase tracking-wider">
              Dit fulde navn <span className="text-[#eebb53]">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={status === 'submitting'}
              className={`w-full px-4 py-3 rounded-lg border text-sm bg-[#FDFBF7] text-[#1E352F] placeholder-[#1E352F]/40 focus:outline-none focus:ring-2 focus:ring-[#1E352F]/20 transition-all ${
                errors.name ? 'border-red-300 focus:border-red-500' : 'border-[#E6DFD3] focus:border-[#1E352F]'
              }`}
              placeholder="F.eks. Mette Jensen"
            />
            {errors.name && <p className="text-xs text-red-600 font-medium">{errors.name}</p>}
          </div>

          {/* Phone & Email Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Phone Field */}
            <div className="space-y-1.5">
              <label htmlFor="phone" className="block text-xs font-semibold text-[#1E352F] uppercase tracking-wider">
                Telefonnummer <span className="text-[#eebb53]">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={status === 'submitting'}
                className={`w-full px-4 py-3 rounded-lg border text-sm bg-[#FDFBF7] text-[#1E352F] placeholder-[#1E352F]/40 focus:outline-none focus:ring-2 focus:ring-[#1E352F]/20 transition-all whitespace-nowrap overflow-x-auto ${
                  errors.phone ? 'border-red-300 focus:border-red-500' : 'border-[#E6DFD3] focus:border-[#1E352F]'
                }`}
                placeholder="F.eks. 86639393"
              />
              {errors.phone && <p className="text-xs text-red-600 font-medium">{errors.phone}</p>}
            </div>

            {/* Email Field */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-xs font-semibold text-[#1E352F] uppercase tracking-wider">
                E-mailadresse <span className="text-[#eebb53]">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={status === 'submitting'}
                className={`w-full px-4 py-3 rounded-lg border text-sm bg-[#FDFBF7] text-[#1E352F] placeholder-[#1E352F]/40 focus:outline-none focus:ring-2 focus:ring-[#1E352F]/20 transition-all whitespace-nowrap overflow-x-auto ${
                  errors.email ? 'border-red-300 focus:border-red-500' : 'border-[#E6DFD3] focus:border-[#1E352F]'
                }`}
                placeholder="F.eks. im@muskelterapi.com"
              />
              {errors.email && <p className="text-xs text-red-600 font-medium">{errors.email}</p>}
            </div>
          </div>

          {/* Description Field */}
          <div className="space-y-1.5">
            <label htmlFor="description" className="block text-xs font-semibold text-[#1E352F] uppercase tracking-wider">
              Beskrivelse / besked <span className="text-[#eebb53]">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              rows="4"
              value={formData.description}
              onChange={handleChange}
              disabled={status === 'submitting'}
              className={`w-full px-4 py-3 rounded-lg border text-sm bg-[#FDFBF7] text-[#1E352F] placeholder-[#1E352F]/40 focus:outline-none focus:ring-2 focus:ring-[#1E352F]/20 transition-all resize-none ${
                errors.description ? 'border-red-300 focus:border-red-500' : 'border-[#E6DFD3] focus:border-[#1E352F]'
              }`}
              placeholder="Beskriv kort dine symptomer, eller hvad du ønsker hjælp til..."
            />
            {errors.description && <p className="text-xs text-red-600 font-medium">{errors.description}</p>}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full bg-[#1E352F] hover:bg-[#2D4A43] text-[#F5F0E6] font-medium py-3.5 px-6 rounded-lg transition-all flex items-center justify-center gap-2 shadow-sm hover:shadow cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sender besked...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send henvendelse
              </>
            )}
          </button>
        </form>
      )}
    </div>
  )
}
